// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ScrollIndicator } from '@/components/scroll-indicator';
import { Search as SearchIcon } from '@/constants/hardfin-standard-icons';
import { Contact } from '@/contact/api/types';
import { useContacts } from '@/contact/hooks/use-contacts';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash-es';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  onContactSelect: (contact: Contact) => void;
  excludeContactId?: string;
  // This company's section is listed before all others
  prioritizedCustomerId?: string;
}

interface CompanyGroup {
  customerId: string;
  customerName: string;
  contacts: Contact[];
}

const SEARCH_DEBOUNCE_MS = 300;
// Fixed height so the list contents never resize the merge dialog
const CONTACT_LIST_HEIGHT_PX = 450;
// -1 is the backend's unpaginated sentinel; the merge picker must be able to
// reach every contact in the organization
const UNPAGINATED_PAGE_SIZE = -1;

export function ContactSelectionList(props: Props) {
  const { contacts } = useContacts({
    filter: { isArchived: 'all' },
    pageSize: UNPAGINATED_PAGE_SIZE,
  });
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const scrollIndicator = useScrollIndicator();
  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
      }, SEARCH_DEBOUNCE_MS),
    [setDebouncedSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      debouncedSetSearch(value);
    },
    [setSearchValue, debouncedSetSearch]
  );

  const companyGroups = useMemo(() => {
    let filtered = contacts.filter(
      (contact) => contact.id !== props.excludeContactId
    );

    if (debouncedSearch.trim() !== '') {
      const search = debouncedSearch.toLowerCase();
      filtered = filtered.filter((contact) => {
        const searchableValues = [
          `${contact.firstName} ${contact.lastName}`,
          contact.customer.name,
          contact.title ?? '',
        ];
        return searchableValues.some((value) =>
          value.toLowerCase().includes(search)
        );
      });
    }

    // Group by company; contacts arrive name-ascending from the API, so
    // each group's contacts stay alphabetically sorted
    const groupsById = new Map<string, CompanyGroup>();
    filtered.forEach((contact) => {
      const group = groupsById.get(contact.customer.id);
      if (group) {
        group.contacts.push(contact);
        return;
      }
      groupsById.set(contact.customer.id, {
        customerId: contact.customer.id,
        customerName: contact.customer.name,
        contacts: [contact],
      });
    });

    const groups = [...groupsById.values()].sort((a, b) =>
      a.customerName.localeCompare(b.customerName)
    );

    // The primary contact's company always leads the list
    const prioritizedIndex = groups.findIndex(
      (group) => group.customerId === props.prioritizedCustomerId
    );
    if (prioritizedIndex > 0) {
      groups.unshift(...groups.splice(prioritizedIndex, 1));
    }

    return groups;
  }, [
    contacts,
    props.excludeContactId,
    props.prioritizedCustomerId,
    debouncedSearch,
  ]);

  return (
    <Stack spacing={2}>
      <TextField
        placeholder="Search contacts"
        value={searchValue}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Paper
        variant="outlined"
        sx={{
          height: CONTACT_LIST_HEIGHT_PX,
          overflow: 'auto',
          position: 'relative',
        }}
        ref={scrollIndicator.ref}
      >
        <ScrollIndicator
          label="More"
          isVisible={scrollIndicator.isVisible}
          onClick={handleClickScroll}
        />
        <Stack spacing={1} sx={{ p: 1 }}>
          {companyGroups.map((group) => (
            <Paper key={group.customerId} variant="outlined">
              <Typography
                variant="subtitle2"
                sx={{ px: 2, pt: 1 }}
                color="text.secondary"
              >
                {group.customerName}
              </Typography>
              <List dense disablePadding>
                {group.contacts.map((contact, index) => (
                  <Box key={contact.id}>
                    <ContactListItem
                      contact={contact}
                      onSelect={() => props.onContactSelect(contact)}
                    />
                    {index < group.contacts.length - 1 && (
                      <Divider sx={{ width: '90%', mx: 'auto' }} />
                    )}
                  </Box>
                ))}
              </List>
            </Paper>
          ))}
          {companyGroups.length === 0 && (
            <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
              <Typography variant="body2" color="text.secondary">
                {debouncedSearch.trim() === ''
                  ? 'No contacts found'
                  : `No contacts found for ${debouncedSearch}`}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

interface ContactListItemProps {
  contact: Contact;
  onSelect: () => void;
}

function ContactListItem(props: ContactListItemProps) {
  const secondaryText = [props.contact.title, props.contact.email]
    .filter(Boolean)
    .join(' · ');

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={props.onSelect}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">
                {props.contact.firstName} {props.contact.lastName}
              </Typography>
              {props.contact.isArchived && (
                <Chip
                  size="small"
                  label="Archived"
                  variant="outlined"
                  color="default"
                />
              )}
            </Box>
          }
          secondary={
            secondaryText !== '' && (
              <Typography variant="caption" color="text.secondary">
                {secondaryText}
              </Typography>
            )
          }
        />
      </ListItemButton>
    </ListItem>
  );
}
